/**
 * Shared helpers for rendering wallet and invoice records.
 *
 * Two things about this data differ from everything else on the dashboard and
 * will produce wrong numbers if treated the usual way:
 *
 *  - Balances are DECIMAL STRINGS in the asset's smallest unit. A balance in
 *    wei exceeds what a double represents exactly, which is why the API stores
 *    `raw` as a string. Never Number() one; format from the string.
 *
 *  - `createdAt` and `lastScanAt` are real Date values, not the unix-ms
 *    strings the rest of the collections use, so they must NOT go through the
 *    usual new Date(Number(v)).
 */

/** Smallest-unit string -> human decimal, without touching a float. */
export function formatUnits(raw, decimals = 18, maxFractionDigits = 8) {
  try {
    const negative = String(raw).startsWith('-');
    const digits = String(raw).replace('-', '').padStart(decimals + 1, '0');
    const whole = digits.slice(0, digits.length - decimals) || '0';
    let fraction = decimals ? digits.slice(digits.length - decimals) : '';

    fraction = fraction.slice(0, maxFractionDigits).replace(/0+$/, '');
    return `${negative ? '-' : ''}${whole}${fraction ? `.${fraction}` : ''}`;
  } catch {
    return '0';
  }
}

/** "0.05 ETH, 120 USDC" from a mirror balances object, or an em-dash. */
export function describeBalances(balances, { max = 3 } = {}) {
  const entries = Object.values(balances || {});
  if (!entries.length) return '—';

  const parts = entries
    .map((b) => `${formatUnits(b.raw, b.decimals)} ${b.symbol}`)
    .slice(0, max);
  if (entries.length > max) parts.push(`+${entries.length - max} more`);
  return parts.join(', ');
}

/** Total held across a set of wallets, in USD, for a stat card. */
export function totalUsd(wallets) {
  return (wallets || []).reduce((sum, w) => sum + (Number(w.lastBalanceUsd) || 0), 0);
}

/** Wallet dates are Dates, not unix-ms strings. */
export function fmtWalletDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

export function fmtRelative(value) {
  if (!value) return 'never';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return 'never';

  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 90) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 90) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/**
 * Block-explorer links per address.
 *
 * Mirrors packages/wallets/derive/chains.js. Monero has no explorer there, so
 * it gets no link here either rather than a dead one.
 */
const EXPLORERS = {
  eth: 'https://etherscan.io',
  btc: 'https://mempool.space',
  ltc: 'https://litecoinspace.org',
};

export function explorerUrl(chain, address) {
  const base = EXPLORERS[chain];
  return base && address ? `${base}/address/${address}` : null;
}

/**
 * Which chains the scanner actually covers.
 *
 * This matters more than it looks: a blank balance on an unscanned chain means
 * "not checked", not "empty", and showing the two the same way invites someone
 * to conclude a wallet is empty when nothing ever looked at it.
 */
export const SCANNED_CHAINS = ['btc', 'eth', 'matic', 'base'];
export const UNSCANNED_NOTE = 'Litecoin has no endpoint configured and Monero cannot be scanned — '
  + 'a blank balance for those means not checked, not empty.';

export const WALLET_STATUS_COLOURS = {
  unassigned: '#64748b',
  awaiting_payment: '#f59e0b',
  paid: '#34d399',
  retired: '#475569',
};

export const INVOICE_STATUS_COLOURS = {
  open: '#f59e0b',
  paid: '#34d399',
  underpaid: '#fb923c',
  expired: '#64748b',
  cancelled: '#475569',
};
